import React, { useEffect, useState } from "react";
import {
  Modal,
  Calendar,
  Input,
  Button,
  Typography,
  Row,
  Col,
  DatePicker,
  AutoComplete,
  App,
} from "antd";
import dayjs from "dayjs";
import "./holidayModal.css";
import { dateOnlyConversion, toCompanyDateOnlyforReportDateFilter } from "../../../../lib/functions";

const { Text } = Typography;

const HolidayModal = ({
  visible,
  onClose,
  onHolidaySubmit,
  //   holidayTypes = [],
  isDisabled = false,
  initialData = null, // for edit mode
}) => {
  const { modal } = App.useApp();

  const [dateTime, setDateTime] = useState(dayjs());
  const [holidayType, setHolidayType] = useState(null);
  const [holidayName, setHolidayName] = useState("");
  const [holidayDate, setHolidayDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [id, setId] = useState(null);

  const holidayTypes = [
    {
      label: "National Holiday",
      value: "national_holiday",
    },
    {
      label: "Regional Holiday",
      value: "regional_holiday",
    },
  ];

  // ⏰ Live clock
  useEffect(() => {
    const timer = setInterval(() => setDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 🎯 Prefill if editing
  useEffect(() => {
    if (initialData) {
      const matchedType = holidayTypes.find(
        (t) => t.value === initialData.holiday_type
      );
      setHolidayType(
        matchedType
          ? { label: matchedType.label, value: matchedType.value }
          : null
      );
      //   setHolidayType({
      //     label: initialData.holiday_type,
      //     value: initialData.holiday_type,
      //   });
      setHolidayName(initialData.holiday_name || "");
      setHolidayDate(
        initialData.holiday_date ? toCompanyDateOnlyforReportDateFilter(initialData.holiday_date) : null
      );
      setNotes(initialData.notes || "");
      setId(initialData.holiday_id);
    } else {
      resetFields();
    }
  }, [initialData, visible]);

  // 🧹 Reset when modal closes
  const resetFields = () => {
    setHolidayType(null);
    setHolidayName("");
    setHolidayDate(null);
    setNotes("");
  };

  // 🧠 Validation + Submit
  const handleSave = () => {
    if (!holidayType)
      return modal.warning({ title: "Select Holiday Type", centered: true });
    if (!holidayName.trim())
      return modal.warning({
        title: "Holiday Name is required",
        centered: true,
      });
    if (!holidayDate)
      return modal.warning({ title: "Select Holiday Date", centered: true });
    if (!notes.trim())
      return modal.warning({ title: "Notes is required", centered: true });

    onHolidaySubmit({
      holiday_type: holidayType.value,
      holiday_name: holidayName.trim(),
      // holiday_date: holidayDate.toISOString(),
      holiday_date: dateOnlyConversion(holidayDate),
      notes: notes.trim(),
      id,
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="holiday-modal"
    >
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">
            {initialData ? "Edit Holiday" : "Holiday"}
          </Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dateTime.format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {/* Calendar */}
      <div className="attendanceOverview-calendar-container" style={{ marginTop: "2rem" }}>
        <Calendar fullscreen={false} />
      </div>

      {/* Form Fields */}
      <Row gutter={16} style={{ marginTop: "2rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            Holiday Type <span className="form-span">*</span>
          </label>
          <AutoComplete
            style={{ width: "100%" }}
            options={holidayTypes.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            value={holidayType?.label}
            onSelect={(value, option) =>
              setHolidayType({ label: option.label, value: option.value })
            }
            placeholder="Select Holiday Type"
             data-testid="holiday_type"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Holiday Name <span className="form-span">*</span>
          </label>
          <Input
            placeholder="Enter Holiday Name"
            value={holidayName}
            onChange={(e) => setHolidayName(e.target.value)}
          data-testid="holiday_name"
        />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: "1.5rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            Holiday Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            // disabled={initialData}
            value={holidayDate}
            onChange={(val) => setHolidayDate(val)}
            format="DD-MMM-YYYY"
            data-testid="holiday_date"
            placeholder="Holiday Date"
          />
        </Col>
        <Col xs={24} sm={12}>
          <label className="form-label">
            Notes <span className="form-span">*</span>
          </label>
          <Input
            placeholder="Notes"
             data-testid="holiday_note"
            value={notes}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 50) setNotes(val);
            }}
            onKeyDown={(e) => {
              if (
                notes &&
                notes.toString().length >= 50 &&
                !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                  e.key
                )
              ) {
                e.preventDefault();
              }
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {notes.length}/50 characters
          </Text>
        </Col>
      </Row>

      {/* Buttons */}
      <div className="attendance-buttons">
        <Button
          onClick={() => {
            resetFields();
            onClose();
          }}
        >
          Discard
        </Button>
        <Button
          type="primary"
          disabled={
            isDisabled || !holidayType || !holidayName || !holidayDate || !notes
          }
          onClick={handleSave}
        >
          {initialData ? "Update Holiday" : "Add Holiday"}
        </Button>
      </div>
    </Modal>
  );
};

export default HolidayModal;
